/**
 * Shared deal pricing for cart, checkout, and reports.
 * @param {Object} params
 * @param {number} params.quantity
 * @param {number} params.regularPrice - unit price before deals
 * @param {Object|null|undefined} params.inventoryData - inventory doc with optional discount_deals[]
 * @param {boolean} [params.includeOriginalPrice=false] - include originalPrice in result (reports)
 * @returns {{ price: number, savings: number, dealApplied: boolean, dealInfo: object|null, originalPrice?: number }}
 */
export function calculateDealPrice({
  quantity,
  regularPrice,
  inventoryData,
  includeOriginalPrice = false,
}) {
  const baseReturn = (price, savings, dealApplied, dealInfo = null) => {
    const result = { price, savings, dealApplied, dealInfo };
    if (includeOriginalPrice) {
      result.originalPrice = regularPrice * quantity;
    }
    return result;
  };

  if (
    !inventoryData ||
    !inventoryData.discount_deals ||
    inventoryData.discount_deals.length === 0
  ) {
    return baseReturn(regularPrice * quantity, 0, false);
  }

  let bestDeal = null;
  let bestSavings = 0;

  inventoryData.discount_deals.forEach((deal) => {
    if (quantity >= deal.quantity) {
      const regularTotal = regularPrice * deal.quantity;
      const savings = regularTotal - deal.price;
      if (savings > bestSavings) {
        bestSavings = savings;
        bestDeal = deal;
      }
    }
  });

  if (!bestDeal) {
    return baseReturn(regularPrice * quantity, 0, false);
  }

  const dealUnits = Math.floor(quantity / bestDeal.quantity);
  const remainingItems = quantity % bestDeal.quantity;
  const dealTotal = dealUnits * bestDeal.price;
  const remainingTotal = remainingItems * regularPrice;
  const totalPrice = dealTotal + remainingTotal;
  const totalSavings = dealUnits * bestSavings;

  return baseReturn(totalPrice, totalSavings, true, {
    dealQuantity: bestDeal.quantity,
    dealPrice: bestDeal.price,
    dealUnits,
    remainingItems,
  });
}
